// src/components/RawMaterials.js
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate, useParams } from 'react-router-dom';
import Loading from './Loading';
import DeleteConfirmation from './DeleteConfirmation';
import ExportConfirmation from './ExportConfirmation';
import SearchableDropdown from './SearchableDropdown';
import API_BASE from '../config';
import { useLanguage } from '../LanguageContext';
const Icon = () => (
  <svg width="28" height="28" viewBox="0 0 24 24" fill="none" style={{ display: 'block' }} xmlns="http://www.w3.org/2000/svg">
    <rect x="3" y="3" width="18" height="4" rx="1" fill="currentColor" />
    <rect x="3" y="10" width="18" height="11" rx="1" fill="currentColor" />
  </svg>
);

const Spinner = ({ size = 40 }) => (
  <svg width={size} height={size} viewBox="0 0 50 50">
    <circle cx="25" cy="25" r="20" stroke="#E9EEFF" strokeWidth="6" fill="none" />
    <path fill="#4318FF" d="M25 5 A20 20 0 0 1 45 25 L41 25 A16 16 0 0 0 25 9z">
      <animateTransform attributeName="transform" type="rotate" from="0 25 25" to="360 25 25" dur="0.9s" repeatCount="indefinite" />
    </path>
  </svg>
);

// RAW_TABS removed as per user request


const pastelBadge = (text, color, dotColor) => (
  <span style={{
    display: 'inline-flex', alignItems: 'center', gap: 6,
    background: color, color: '#303030', borderRadius: 999,
    fontWeight: 500, fontSize: 12, padding: '3px 14px'
  }}>
    {dotColor && <span style={{
      display: 'inline-block', width: 8, height: 8, borderRadius: 99, background: dotColor
    }} />}
    {text}
  </span>
);

const RawMaterials = () => {
  const navigate = useNavigate();
  const { workspaceUuid } = useParams();
  const { t, isRtl } = useLanguage();
  const [materials, setMaterials] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [suppliers, setSuppliers] = useState([]);
  const [filterName, setFilterName] = useState('');
  const [approvedMaterials, setApprovedMaterials] = useState([]);
  const [form, setForm] = useState({
    material_name: '',
    material_code: '',
    batch_number: '',
    quantity: '',
    manufacturing_date: '',
    receiving_date: '',
    supplier_name: '',
    supplierId: '',
    supplierSelectedId: '' // supplier._id from suppliers list
  });
  // menu state for three-dot menus
  const [menuOpenId, setMenuOpenId] = useState(null);
  const [menuPos, setMenuPos] = useState({ x: 0, y: 0 });
  const [deleteConfirm, setDeleteConfirm] = useState({ open: false, id: null, name: '' });
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [exportConfirm, setExportConfirm] = useState(false);
  const [isMobile, setIsMobile] = useState(typeof window !== 'undefined' ? window.innerWidth <= 720 : false);
  useEffect(() => { const onResize = () => setIsMobile(window.innerWidth <= 720); window.addEventListener('resize', onResize); return () => window.removeEventListener('resize', onResize); }, []);

  const apiBase = `${API_BASE}/rawmaterials`;

  const getAuthHeaders = () => {
    const token = localStorage.getItem('token');
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  const fetchSuppliers = async () => {
    try {
      const res = await axios.get(`${API_BASE}/suppliers`, { headers: getAuthHeaders() });
      setSuppliers(res.data || []);
    } catch (err) {
      console.error('Failed to fetch suppliers', err);
    }
  };

  const fetchApprovedMaterials = async () => {
    try {
      const res = await axios.get(`${API_BASE}/approved-raw-materials`, { headers: getAuthHeaders() });
      setApprovedMaterials(res.data || []);
    } catch (err) {
      console.error('Failed to fetch approved materials', err);
    }
  };

  const fetchMaterials = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await axios.get(apiBase, { headers: getAuthHeaders() });
      setMaterials(res.data || []);
    } catch (err) {
      setError(err.response?.data?.msg || t.failed_fetch_raw);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMaterials();
    fetchSuppliers();
    fetchApprovedMaterials();
  }, []);

  useEffect(() => {
    const onDocClick = (e) => {
      if (!e.target.closest('.floating-menu') && !e.target.closest('.menu-btn')) setMenuOpenId(null);
    };
    document.addEventListener('click', onDocClick);
    return () => document.removeEventListener('click', onDocClick);
  }, []);

  const openAdd = () => {
    setIsEditing(false);
    setEditingId(null);
    setForm({
      material_name: '',
      material_code: '',
      batch_number: '',
      quantity: '',
      manufacturing_date: '',
      receiving_date: '',
      supplier_name: '',
      supplierId: '',
      supplierSelectedId: ''
    });
    setModalOpen(true);
  };

  const openEdit = (m) => {
    setIsEditing(true);
    setEditingId(m._id || m.id);
    // try to pre-select supplier by supplier_code (m.supplierId) or by supplier_name
    let selectedId = '';
    if (m.supplierId) {
      const byCode = suppliers.find(s => s.supplier_code === m.supplierId);
      if (byCode) selectedId = byCode._id;
    }
    if (!selectedId && m.supplier_name) {
      const byName = suppliers.find(s => s.supplier_name === m.supplier_name);
      if (byName) selectedId = byName._id;
    }
    setForm({
      material_name: m.material_name || '',
      material_code: m.material_code || '',
      batch_number: m.batch_number || '',
      quantity: m.actual_quantity != null ? String(m.actual_quantity) : '',
      manufacturing_date: m.manufacturing_date ? new Date(m.manufacturing_date).toISOString().slice(0, 10) : '',
      receiving_date: m.receiving_date ? new Date(m.receiving_date).toISOString().slice(0, 10) : '',
      supplier_name: m.supplier_name || '',
      supplierId: m.supplierId || '',
      supplierSelectedId: selectedId
    });
    setModalOpen(true);
  };

  const handleDelete = (m) => {
    setDeleteConfirm({ open: true, id: m._id, name: `${m.material_name} (${m.batch_number})` });
  };

  const executeDelete = async () => {
    setDeleting(true);
    try {
      await axios.delete(`${apiBase}/${deleteConfirm.id}`, { headers: getAuthHeaders() });
      setDeleteConfirm({ open: false, id: null, name: '' });
      fetchMaterials();
    } catch (err) {
      alert(err.response?.data?.msg || t.delete_failed);
      setDeleteConfirm({ open: false, id: null, name: '' });
    } finally {
      setDeleting(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    // basic validation
    if (!form.material_name || !form.material_code || !form.batch_number || !form.quantity) {
      alert(t.fill_required_fields_raw);
      return;
    }
    const payload = {
      material_name: form.material_name,
      material_code: form.material_code,
      batch_number: form.batch_number,
      quantity: Number(form.quantity)
    };
    if (form.manufacturing_date) payload.manufacturing_date = form.manufacturing_date;
    if (form.receiving_date) payload.receiving_date = form.receiving_date;
    // if a supplier is selected, use supplier data from suppliers list
    if (form.supplierSelectedId) {
      const sel = suppliers.find(s => s._id === form.supplierSelectedId);
      if (sel) {
        payload.supplier_name = sel.supplier_name;
        payload.supplierId = sel.supplier_code; // store supplier code as supplierId (string)
      }
    } else {
      // fallback to any typed supplier fields (should be uncommon)
      if (form.supplier_name) payload.supplier_name = form.supplier_name;
      if (form.supplierId) payload.supplierId = form.supplierId;
    }
    setSaving(true);
    try {
      if (isEditing && editingId) {
        await axios.put(`${apiBase}/${editingId}`, payload, { headers: getAuthHeaders() });
      } else {
        await axios.post(apiBase, payload, { headers: getAuthHeaders() });
      }
      setModalOpen(false);
      fetchMaterials();
    } catch (err) {
      alert(err.response?.data?.msg || t.save_failed);
    } finally {
      setSaving(false);
    }
  };

  const openDetail = (m) => {
    if (!m.uuid) return;
    navigate(`/workspace/${workspaceUuid}/raw-materials/${m.uuid}`);
  };


  const downloadCSV = () => {
    const rows = filteredMaterials.map(m => [
      `"${m.material_name || ''}"`,
      `"${m.material_code || ''}"`,
      `"${m.batch_number || ''}"`,
      m.available_quantity,
      `"${formatDate(m.manufacturing_date)}"`,
      `"${formatDate(m.receiving_date)}"`,
      `"${m.supplier_name || '-'}"`
    ]);

    const csvContent = [
      [t.raw_material, t.code, t.batch, t.quantity, t.mfg_date, t.receiving_date, t.supplier].join(','),
      ...rows.map(r => r.join(','))
    ].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', `raw_materials_${new Date().toISOString().slice(0, 10)}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const formatDate = (d) => d ? new Date(d).toLocaleDateString(isRtl ? 'ar-EG' : 'en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '-';

  const filteredMaterials = materials.filter(m => {
    const term = filterName.toLowerCase();
    const matchSearch = term === '' ||
      (m.material_name?.toLowerCase().includes(term)) ||
      (m.material_code?.toLowerCase().includes(term)) ||
      (m.batch_number?.toLowerCase().includes(term)) ||
      (m.supplier_name?.toLowerCase().includes(term)) ||
      (formatDate(m.manufacturing_date)?.toLowerCase().includes(term)) ||
      (formatDate(m.receiving_date)?.toLowerCase().includes(term));

    return matchSearch;
  });

  return (
    <div style={{ maxWidth: 1200, margin: '0 auto', paddingBottom: 60, direction: isRtl ? 'rtl' : 'ltr', textAlign: isRtl ? 'right' : 'left' }}>
      {/* Search Bar / Top Status Placeholder (Shopify layout) */}

      {/* Page Title & Primary Action */}
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        marginBottom: 20, flexDirection: isRtl ? 'row-reverse' : 'row'
      }}>
        <h1 style={{ margin: 0, fontSize: 28, fontWeight: 700, color: '#1a1c1d' }}>{t.raw_materials}</h1>
        <div style={{ display: 'flex', gap: 8, flexDirection: isRtl ? 'row-reverse' : 'row' }}>
          <button onClick={() => setExportConfirm(true)} style={{
            padding: '8px 12px', borderRadius: 6, border: '1px solid #d2d2d2',
            background: '#fff', color: '#1a1c1d', fontWeight: 600, fontSize: 13,
            cursor: 'pointer', transition: 'box-shadow 0.2s'
          }}>
            {t.export}
          </button>
          <button onClick={openAdd} style={{
            padding: '8px 16px', borderRadius: 6, background: '#1976d2', color: '#fff',
            fontWeight: 700, fontSize: 13, border: 'none', cursor: 'pointer',
            boxShadow: '0 1px 0 rgba(0,0,0,0.05)', display: 'flex', alignItems: 'center'
          }}>
            {t.add_raw_material}
          </button>
        </div>
      </div>

      {/* Main Content Card (IndexTable container) */}
      <div style={{
        background: '#fff',
        borderRadius: 12,
        boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1), 0 1px 2px rgba(0, 0, 0, 0.06)',
        border: '1px solid #ebebeb',
        overflow: 'hidden'
      }}>
        <div style={{
          padding: '0 16px',
          background: '#fff'
        }}>
          {/* Tabs removed as per user request */}
        </div>

        {/* Filter Bar */}
        <div style={{
          padding: '12px 16px',
          display: 'flex',
          gap: 12,
          alignItems: 'center',
          background: '#fff',
          borderBottom: '1px solid #f1f1f1',
          flexDirection: isRtl ? 'row-reverse' : 'row'
        }}>
          <div style={{ position: 'relative', flex: 1 }}>
            <span style={{ position: 'absolute', [isRtl ? 'right' : 'left']: 10, top: '50%', transform: 'translateY(-50%)', color: '#6d7175' }}>
              <svg width="16" height="16" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z" clipRule="evenodd" /></svg>
            </span>
            <input
              type="text"
              value={filterName}
              onChange={(e) => setFilterName(e.target.value)}
              placeholder={t.filter_raw_materials}
              style={{
                width: '100%', padding: isRtl ? '8px 34px 8px 12px' : '8px 12px 8px 34px', borderRadius: 8,
                border: '1px solid #d2d2d2', fontSize: 13, color: '#1a1c1d',
                outline: 'none', background: '#fff',
                textAlign: isRtl ? 'right' : 'left'
              }}
            />
          </div>

        </div>

        {/* IndexTable */}
        {loading ? (
          <Loading message={t.fetching_raw} />
        ) : (
          <div className="fade-in" style={{ overflowX: 'auto' }}>
            <table style={{
              width: '100%',
              borderCollapse: 'collapse',
              fontSize: 13,
              color: '#1a1c1d'
            }}>
              <thead>
                <tr style={{ background: '#f9fafb', height: 40, borderBottom: '1px solid #ebebeb' }}>
                  <th style={{ width: 40 }}></th>
                  <th style={{ textAlign: isRtl ? 'right' : 'left', padding: '10px 16px', fontWeight: 600 }}>{t.raw_material}</th>
                  <th style={{ textAlign: isRtl ? 'right' : 'left', padding: '10px 16px', fontWeight: 600 }}>{t.code}</th>
                  <th style={{ textAlign: isRtl ? 'right' : 'left', padding: '10px 16px', fontWeight: 600 }}>{t.batch}</th>
                  <th style={{ textAlign: isRtl ? 'left' : 'right', padding: '10px 16px', fontWeight: 600 }}>{t.stock_rem_total || t.stock}</th>
                  <th style={{ textAlign: isRtl ? 'right' : 'left', padding: '10px 16px', fontWeight: 600 }}>{t.mfg_date}</th>
                  <th style={{ textAlign: isRtl ? 'right' : 'left', padding: '10px 16px', fontWeight: 600 }}>{t.receiving_date}</th>
                  <th style={{ textAlign: isRtl ? 'right' : 'left', padding: '10px 16px', fontWeight: 600 }}>{t.supplier}</th>
                  <th style={{ width: 60 }} />
                </tr>
              </thead>
              <tbody>
                {filteredMaterials.length === 0 ? (
                  <tr>
                    <td colSpan={9} style={{ padding: 48, textAlign: 'center', color: '#6d7175' }}>
                      <div style={{ marginBottom: 12 }}>
                        <svg width="48" height="48" fill="none" viewBox="0 0 20 20" style={{ margin: '0 auto', color: '#babfc3' }}><path d="M10 2C5.58 2 2 5.58 2 10s3.58 8 8 8 8-3.58 8-8-3.58-8-8-8zm0 14c-3.31 0-6-2.69-6-6s2.69-6 6-6 6 2.69 6 6-2.69 6-6 6z" fill="currentColor" /><path d="M10 5a1 1 0 011 1v4a1 1 0 11-2 0V6a1 1 0 011-1zM10 13a1.25 1.25 0 100 2.5 1.25 1.25 0 000-2.5z" fill="currentColor" /></svg>
                      </div>
                      {t.no_raw_found}
                    </td>
                  </tr>
                ) : filteredMaterials.map((m) => {
                  return (
                    <tr
                      key={m._id || m.uuid}
                      style={{
                        borderBottom: '1px solid #f1f1f1',
                        transition: 'background 0.1s'
                      }}
                      onMouseOver={e => e.currentTarget.style.background = '#f9fafb'}
                      onMouseOut={e => e.currentTarget.style.background = '#fff'}
                    >
                      <td style={{ textAlign: 'center', padding: '12px 0' }}>
                        <button
                          onClick={() => openDetail(m)}
                          style={{
                            background: 'none', border: 'none', cursor: 'pointer',
                            color: '#2c6ecb', display: 'flex', alignItems: 'center', justifyContent: 'center',
                            margin: '0 auto', padding: 4, borderRadius: 4
                          }}
                          onMouseOver={e => e.currentTarget.style.background = '#eef4ff'}
                          onMouseOut={e => e.currentTarget.style.background = 'transparent'}
                          title={t.view_details}
                        >
                          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"></path><polyline points="15 3 21 3 21 9"></polyline><line x1="10" y1="14" x2="21" y2="3"></line></svg>
                        </button>
                      </td>
                      <td style={{ padding: '12px 16px', fontWeight: 600, textAlign: isRtl ? 'right' : 'left' }}>{m.material_name}</td>
                      <td style={{ padding: '12px 16px', color: '#6d7175', textAlign: isRtl ? 'right' : 'left' }}>{m.material_code}</td>
                      <td style={{ padding: '12px 16px', color: '#6d7175', textAlign: isRtl ? 'right' : 'left' }}>{m.batch_number}</td>
                      <td style={{ padding: '12px 16px', textAlign: isRtl ? 'left' : 'right', fontWeight: 600, direction: 'ltr' }}>
                        <span style={{ color: '#1976d2' }}>{Number(m.available_quantity || 0).toLocaleString(isRtl ? 'ar-EG' : undefined, { maximumFractionDigits: 2 })}</span>
                        <span style={{ color: '#6d7175', margin: '0 4px', fontWeight: 400 }}>/</span>
                        <span style={{ color: '#1a1c1d' }}>{Number(m.actual_quantity || 0).toLocaleString(isRtl ? 'ar-EG' : undefined, { maximumFractionDigits: 2 })}</span>
                        <span style={{ marginLeft: 4, fontSize: 11, color: '#6d7175', fontWeight: 400 }}>{t.kg}</span>
                      </td>
                      <td style={{ padding: '12px 16px', color: '#6d7175', textAlign: isRtl ? 'right' : 'left' }}>{formatDate(m.manufacturing_date)}</td>
                      <td style={{ padding: '12px 16px', color: '#6d7175', textAlign: isRtl ? 'right' : 'left' }}>{formatDate(m.receiving_date)}</td>
                      <td style={{ padding: '12px 16px', color: '#6d7175', textAlign: isRtl ? 'right' : 'left' }}>{m.supplier_name || '-'}</td>
                      <td style={{ textAlign: isRtl ? 'right' : 'left' }} onClick={e => e.stopPropagation()}>
                        <button onClick={e => {
                          e.stopPropagation();
                          const rect = e.currentTarget.getBoundingClientRect();
                          setMenuPos({ x: isRtl ? rect.right : rect.left, y: rect.bottom + 8 });
                          setMenuOpenId(m._id);
                        }}
                          className="menu-btn"
                          style={{
                            background: 'transparent', border: 'none', cursor: 'pointer',
                            color: '#6d7175', padding: 8, borderRadius: 4
                          }}>
                          <svg width="20" height="20" viewBox="0 0 20 20"><path d="M6 10a2 2 0 11-4 0 2 2 0 014 0zM12 10a2 2 0 11-4 0 2 2 0 014 0zM18 10a2 2 0 11-4 0 2 2 0 014 0z" fill="currentColor" /></svg>
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Floating Menu */}
      {
        menuOpenId && (() => {
          const it = materials.find(x => x._id === menuOpenId);
          if (!it) return null;
          return (
            <div className="floating-menu" style={{
              position: 'fixed',
              [isRtl ? 'right' : 'left']: isRtl ? (window.innerWidth - menuPos.x) : (menuPos.x - 100),
              top: menuPos.y, background: '#fff',
              borderRadius: 8, boxShadow: '0 12px 30px rgba(0,0,0,0.15)', minWidth: 160, zIndex: 2000,
              border: '1px solid #ebebeb'
            }} onClick={e => e.stopPropagation()}>
              <div style={{ display: 'flex', flexDirection: 'column', padding: 4 }}>
                <button onClick={() => { setMenuOpenId(null); openEdit(it); }} style={{
                  padding: '8px 12px', textAlign: isRtl ? 'right' : 'left', border: 'none', background: 'transparent',
                  cursor: 'pointer', color: '#1a1c1d', fontSize: 13, borderRadius: 4
                }} onMouseOver={e => e.target.style.background = '#f1f1f1'} onMouseOut={e => e.target.style.background = 'transparent'}>{t.edit}</button>
                <button onClick={() => { setMenuOpenId(null); handleDelete(it); }} style={{
                  padding: '8px 12px', textAlign: isRtl ? 'right' : 'left', border: 'none', background: 'transparent',
                  cursor: 'pointer', color: '#d82c0d', fontSize: 13, borderRadius: 4
                }} onMouseOver={e => e.target.style.background = '#f1f1f1'} onMouseOut={e => e.target.style.background = 'transparent'}>{t.delete}</button>
              </div>
            </div>
          );
        })()
      }

      {/* Modal - Styled like Shopify/Polaris */}
      {
        modalOpen && (
          <div className="overlay-fade-in" style={{ position: 'fixed', inset: 0, background: 'rgba(26, 28, 29, 0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1200 }}>
            <div className="modal-fade-in" style={{ width: 600, background: '#fff', borderRadius: 12, boxShadow: '0 28px 60px rgba(0,0,0,0.2)', overflow: 'hidden', direction: isRtl ? 'rtl' : 'ltr', textAlign: isRtl ? 'right' : 'left' }}>
              <div style={{ padding: '16px 20px', borderBottom: '1px solid #ebebeb', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexDirection: isRtl ? 'row-reverse' : 'row' }}>
                <h3 style={{ margin: 0, fontSize: 16, fontWeight: 600, color: '#1a1c1d' }}>{isEditing ? t.edit_raw_material : t.add_raw_material}</h3>
                <button onClick={() => setModalOpen(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#6d7175' }}>
                  <svg width="20" height="20" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" /></svg>
                </button>
              </div>
              <form onSubmit={handleSubmit}>
                <div style={{ padding: 20 }}>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                      <div style={{ textAlign: isRtl ? 'right' : 'left' }}>
                        <label style={labelStyle}>{t.material_name}*</label>
                        <SearchableDropdown
                          showCheckbox={false}
                          options={approvedMaterials.map(am => ({ value: am.name, label: am.name }))}
                          value={form.material_name}
                          onChange={(val) => {
                            const selected = approvedMaterials.find(am => am.name === val);
                            setForm(prev => ({
                              ...prev,
                              material_name: val,
                              material_code: selected ? (selected.code || selected.uuid) : prev.material_code
                            }));
                          }}
                          placeholder={t.select_material}
                        />
                      </div>
                      <div style={{ textAlign: isRtl ? 'right' : 'left' }}>
                        <label style={labelStyle}>{t.material_code}*</label>
                        <SearchableDropdown
                          showCheckbox={false}
                          options={approvedMaterials.map(am => ({ value: am.code || am.uuid, label: am.code || am.uuid }))}
                          value={form.material_code}
                          onChange={(val) => {
                            const selected = approvedMaterials.find(am => (am.code || am.uuid) === val);
                            setForm(prev => ({
                              ...prev,
                              material_code: val,
                              material_name: selected ? selected.name : prev.material_name
                            }));
                          }}
                          placeholder={t.select_code}
                        />
                      </div>
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                      <div style={{ textAlign: isRtl ? 'right' : 'left' }}>
                        <label style={labelStyle}>{t.batch_number}*</label>
                        <input name="batch_number" value={form.batch_number} onChange={handleChange} style={{ ...inputStyle, textAlign: isRtl ? 'right' : 'left' }} required />
                      </div>
                      <div style={{ textAlign: isRtl ? 'right' : 'left' }}>
                        <label style={labelStyle}>{t.quantity} ({t.kg})*</label>
                        <input name="quantity" type="number" value={form.quantity} onChange={handleChange} style={{ ...inputStyle, textAlign: isRtl ? 'right' : 'left' }} required />
                      </div>
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                      <div style={{ textAlign: isRtl ? 'right' : 'left' }}>
                        <label style={labelStyle}>{t.manufacturing_date}</label>
                        <input name="manufacturing_date" type="date" value={form.manufacturing_date} onChange={handleChange} style={{ ...inputStyle, textAlign: isRtl ? 'right' : 'left' }} />
                      </div>
                      <div style={{ textAlign: isRtl ? 'right' : 'left' }}>
                        <label style={labelStyle}>{t.receiving_date}</label>
                        <input name="receiving_date" type="date" value={form.receiving_date} onChange={handleChange} style={{ ...inputStyle, textAlign: isRtl ? 'right' : 'left' }} />
                      </div>
                    </div>
                    <div style={{ textAlign: isRtl ? 'right' : 'left' }}>
                      <label style={labelStyle}>{t.supplier}</label>
                      <SearchableDropdown
                        showCheckbox={false}
                        options={suppliers.map(s => ({ value: s._id, label: s.supplier_name }))}
                        value={form.supplierSelectedId}
                        onChange={(val) => setForm(prev => ({ ...prev, supplierSelectedId: val }))}
                        placeholder={t.select_supplier}
                      />
                    </div>
                  </div>
                </div>
                 <div style={{ padding: '16px 20px', borderTop: '1px solid #ebebeb', display: 'flex', justifyContent: 'flex-end', gap: 12, background: '#f9fafb', flexDirection: isRtl ? 'row-reverse' : 'row' }}>
                  <button type="button" onClick={() => setModalOpen(false)} style={{ padding: '8px 16px', borderRadius: 8, border: '1px solid #d2d2d2', background: '#fff', color: '#1a1c1d', fontWeight: 600, fontSize: 13, cursor: 'pointer' }}>{t.cancel}</button>
                  <button type="submit" disabled={saving} style={{ padding: '8px 20px', borderRadius: 8, border: 'none', background: '#1976d2', color: '#fff', fontWeight: 700, fontSize: 13, cursor: saving ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', minWidth: 140 }}>
                    {saving ? <div className="dots-loading"><span></span><span></span><span></span></div> : (isEditing ? t.save : t.create_raw_material)}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )
      }
      {
        deleteConfirm.open && (
          <DeleteConfirmation
            onConfirm={executeDelete}
            onCancel={() => setDeleteConfirm({ open: false, id: null, name: '' })}
            itemName={deleteConfirm.name}
            isDeleting={deleting}
          />
        )
      }
      {
        exportConfirm && (
          <ExportConfirmation
            itemName={t.raw_materials}
            onConfirm={() => { downloadCSV(); setExportConfirm(false); }}
            onCancel={() => setExportConfirm(false)}
          />
        )
      }
    </div >
  );
};

const labelStyle = { display: 'block', fontSize: 13, fontWeight: 500, color: '#1a1c1d', marginBottom: 4 };
const inputStyle = { width: '100%', padding: '8px 12px', borderRadius: 8, border: '1px solid #d2d2d2', fontSize: 13, outline: 'none', background: '#fff' };

export default RawMaterials;

// Inline SVG icon components to replace react-icons/fi
const PlusIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
    <path d="M12 5v14M5 12h14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
  </svg>
);
const EditIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
    <path d="M4 21h4.586a1 1 0 0 0 .707-.293l10.414-10.414a2 2 0 0 0 0-2.828l-2.172-2.172a2 2 0 0 0-2.828 0L4.293 15.293A1 1 0 0 0 4 16v4z" stroke="#64748b" strokeWidth="1.5" fill="none" />
  </svg>
);
const TrashIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
    <path d="M3 6h18M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2m2 0v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6h16z" stroke="#ef4444" strokeWidth="1.5" fill="none" />
  </svg>
);
const ChevronDownIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
    <path d="M6 9l6 6 6-6" stroke="#303030" strokeWidth="1.5" fill="none" />
  </svg>
);